import { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/* ── Google "G" SVG ────────────────────────────────────────── */
const GoogleLogo = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

/* ── Kiddo+ logo ────────────────────────────────────────────── */
const LOGO     = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

/* ── Google-colored spinning ring (CSS only) ────────────────── */
function GoogleSpinRing({ size = 96 }) {
  const r = size / 2;
  return (
    <svg
      width={size + 16}
      height={size + 16}
      viewBox={`0 0 ${size + 16} ${size + 16}`}
      style={{ animation: "gc-spin 1.4s linear infinite" }}
      aria-hidden="true"
    >
      {/* Four colored arcs, each ~90° */}
      <circle cx={r + 8} cy={r + 8} r={r} fill="none" strokeWidth="3.5"
        stroke="#FFC107" strokeDasharray={`${(Math.PI * size) / 4} ${(Math.PI * size * 3) / 4}`}
        strokeDashoffset="0" strokeLinecap="round" />
      <circle cx={r + 8} cy={r + 8} r={r} fill="none" strokeWidth="3.5"
        stroke="#FF3D00" strokeDasharray={`${(Math.PI * size) / 4} ${(Math.PI * size * 3) / 4}`}
        strokeDashoffset={`-${(Math.PI * size) / 4}`} strokeLinecap="round" />
      <circle cx={r + 8} cy={r + 8} r={r} fill="none" strokeWidth="3.5"
        stroke="#4CAF50" strokeDasharray={`${(Math.PI * size) / 4} ${(Math.PI * size * 3) / 4}`}
        strokeDashoffset={`-${(Math.PI * size) / 2}`} strokeLinecap="round" />
      <circle cx={r + 8} cy={r + 8} r={r} fill="none" strokeWidth="3.5"
        stroke="#1976D2" strokeDasharray={`${(Math.PI * size) / 4} ${(Math.PI * size * 3) / 4}`}
        strokeDashoffset={`-${(Math.PI * size * 3) / 4}`} strokeLinecap="round" />
    </svg>
  );
}

/* ── Full-screen connecting overlay ────────────────────────── */
function GoogleConnectingOverlay() {
  return (
    <>
      <style>{`
        @keyframes gc-fade-in  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes gc-slide-up {
          from { opacity: 0; transform: translateY(28px) scale(0.97) }
          to   { opacity: 1; transform: translateY(0) scale(1) }
        }
        @keyframes gc-spin     { to { transform: rotate(360deg) } }
        @keyframes gc-pulse    {
          0%,100% { opacity: 1 }
          50%     { opacity: 0.4 }
        }
        @keyframes gc-dot-flow {
          0%   { transform: translateX(0);   opacity: 0 }
          20%  { opacity: 1 }
          80%  { opacity: 1 }
          100% { transform: translateX(52px); opacity: 0 }
        }
        @keyframes gc-progress {
          0%   { transform: translateX(-100%) scaleX(0.4) }
          50%  { transform: translateX(80%)  scaleX(1)   }
          100% { transform: translateX(200%) scaleX(0.4) }
        }
        .gc-overlay   { animation: gc-fade-in  .22s ease both }
        .gc-card      { animation: gc-slide-up .38s cubic-bezier(.22,1,.36,1) .06s both }
        .gc-pulse-txt { animation: gc-pulse 2s ease-in-out infinite }
        .gc-dot-1 { animation: gc-dot-flow 1.4s ease-in-out infinite }
        .gc-dot-2 { animation: gc-dot-flow 1.4s ease-in-out .47s infinite }
        .gc-dot-3 { animation: gc-dot-flow 1.4s ease-in-out .94s infinite }

        /* ── dark-mode overrides ── */
        .gc-bg          { background: #EBF5F2 }
        .gc-card-bg     { background: #ffffff }
        .gc-header-band { background: linear-gradient(135deg, #EBF5F2 0%, #d4ede8 100%) }
        .gc-logo-circle { background: #ffffff }
        .gc-icon-circle { background: #ffffff; border-color: #f3f4f6 }
        .gc-heading     { color: #1A2E2A }
        .gc-subtext     { color: #4A5568 }
        .gc-prog-track  { background: #C2E0DA }

        .dark .gc-bg          { background: #091714 }
        .dark .gc-card-bg     { background: #161C24 }
        .dark .gc-header-band { background: linear-gradient(135deg, #0d2420 0%, #0a1c18 100%) }
        .dark .gc-logo-circle { background: #1E252E }
        .dark .gc-icon-circle { background: #1E252E; border-color: #2A323C }
        .dark .gc-heading     { color: #DFF2EE }
        .dark .gc-subtext     { color: #94A3B8 }
        .dark .gc-prog-track  { background: rgba(20,184,166,0.15) }
      `}</style>

      <div
        className="gc-overlay gc-bg fixed inset-0 z-[400] flex items-center justify-center px-4"
        data-testid="google-connecting-overlay"
      >
        {/* Subtle grid-dot background */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, #2A7B6F 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

        <div className="gc-card gc-card-bg relative w-full max-w-sm rounded-3xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.12)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.5)]">

          {/* Teal header band */}
          <div className="gc-header-band relative pt-10 pb-12 flex flex-col items-center overflow-hidden">
            <div className="relative flex items-center justify-center">
              <div className="absolute">
                <GoogleSpinRing size={88} />
              </div>
              <div className="gc-logo-circle relative z-10 w-[88px] h-[88px] rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.1)] flex items-center justify-center">
                <GoogleLogo className="w-10 h-10" />
              </div>
            </div>
          </div>

          {/* Card body */}
          <div className="gc-card-bg -mt-6 rounded-t-[2rem] pt-5 pb-8 px-6 flex flex-col items-center text-center">

            {/* Animated flow: Google → Kiddo+ */}
            <div className="flex items-center gap-3 mb-5 mt-1">
              <div className="gc-icon-circle w-9 h-9 rounded-full border shadow-sm flex items-center justify-center shrink-0">
                <GoogleLogo className="w-5 h-5" />
              </div>

              {/* Flowing dots */}
              <div className="relative w-14 h-4 flex items-center overflow-hidden shrink-0">
                {[0,1,2].map(i => (
                  <span key={i}
                    className={`gc-dot-${i+1} absolute left-0 w-2.5 h-2.5 rounded-full`}
                    style={{ background: ["#FFC107","#4CAF50","#1976D2"][i], top: "50%", marginTop: "-5px" }}
                  />
                ))}
              </div>

              <div className="gc-icon-circle w-9 h-9 rounded-full border shadow-sm flex items-center justify-center shrink-0">
                <img src={LOGO} alt="Kiddo+" className="w-6 h-6 rounded-full object-cover" />
              </div>
            </div>

            <h2 className="gc-heading font-kp font-extrabold text-[20px] leading-tight">
              Connecting your account
            </h2>
            <p className="gc-subtext gc-pulse-txt text-[13px] mt-1.5">
              Signing you in with Google…
            </p>

            {/* Progress bar */}
            <div className="gc-prog-track mt-5 w-full h-1 rounded-full overflow-hidden">
              <div className="h-full rounded-full origin-left"
                style={{
                  background: "linear-gradient(90deg, #4CAF50, #1976D2, #FFC107, #FF3D00)",
                  backgroundSize: "200% 100%",
                  width: "60%",
                  marginLeft: "-10%",
                  animation: "gc-progress 1.8s ease-in-out infinite",
                }}
              />
            </div>

            {/* Kiddo+ wordmark */}
            <img src={WORDMARK} alt="Kiddo+" className="mt-6 h-5 w-auto opacity-40 dark:brightness-0 dark:invert dark:opacity-30" />
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Main button ────────────────────────────────────────────── */
/**
 * Modern "Continue with Google" button.
 *
 * Uses the implicit (token) flow from @react-oauth/google so we get full
 * control over the button styling while still letting Google handle the
 * popup + consent screen. The access_token is forwarded to our backend
 * which verifies it against Google's userinfo endpoint and issues our
 * own JWT.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleSignInButton({ label = "Continue with Google", className = "", style = {} }) {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const [loading, setLoading]       = useState(false);
  const [connecting, setConnecting] = useState(false); // full-screen phase

  const startLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setConnecting(true); // show full-screen overlay while API resolves
      try {
        const res = await loginWithGoogle(tokenResponse.access_token);
        toast.success(res.is_new ? "Welcome to Kiddo+ 🌿" : "Welcome back!");
        if (nextPath && nextPath.startsWith("/")) {
          navigate(nextPath);
        } else if (res.user?.role === "admin") {
          navigate("/admin");
        } else if (res.is_new) {
          navigate("/onboarding");
        } else {
          navigate("/activities");
        }
      } catch (err) {
        setConnecting(false);
        toast.error(err?.response?.data?.detail || "Google sign-in failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setLoading(false);
      toast.error("Google sign-in was cancelled.");
    },
  });

  const onClick = () => {
    setLoading(true);
    startLogin();
  };

  return (
    <>
      {connecting && <GoogleConnectingOverlay />}

      <button
        type="button"
        onClick={onClick}
        disabled={loading || connecting}
        data-testid="google-signin-btn"
        style={style}
        className={
          "google-pulse " +
          "group relative w-full inline-flex items-center justify-center gap-3 " +
          "rounded-full py-3 px-5 font-semibold text-sm " +
          "text-ink border border-plum/15 " +
          "shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)] " +
          "hover:border-plum/25 hover:-translate-y-[1px] active:translate-y-0 " +
          "transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed " +
          className
        }
      >
        <GoogleLogo className="w-5 h-5 shrink-0" />
        <span>{loading ? "Connecting to Google…" : label}</span>
      </button>
    </>
  );
}
